import { Stack } from 'braid-design-system';
import { observer } from 'mobx-react';
import { type FC, type ReactNode, useEffect, useState } from 'react';
import Filters from 'src/components/common/Filters/Filters';
import type { FilterFns } from './useFilters';
import type { IFilterQuestion } from 'src/stores/JobApplicationStore/FiltersStore/FiltersStore';
import FilterSearchBar from 'src/components/common/SearchBar/FilterSearchBar';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';
import { useStores } from 'src/utils/hooks/useStores';
import { BucketSelect } from './BucketSelect';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';

interface FilterSideBarContentProps {
  onMustHaveFilterToggled: FilterFns['onMustHaveFilterToggled'];
  onParentFilterSelected: FilterFns['onParentFilterSelected'];
  onFilterSelected: FilterFns['onFilterSelected'];
  clearFilters: () => void;
  isMustHaveToggleSelected: boolean;
  questions: IFilterQuestion[];
}

export const FilterSideBarContent: FC<FilterSideBarContentProps> = observer(
  ({
    onMustHaveFilterToggled,
    onParentFilterSelected,
    onFilterSelected,
    clearFilters,
    isMustHaveToggleSelected,
    questions,
  }) => {
    const { t } = useTranslations(translations);
    const { jobApplicationStore } = useStores();
    const [, setQueryQueryParams] = useQueryParamsStore();
    const [isReadyOnClient, setIsReadyOnClient] = useState(false);
    useEffect(() => {
      setIsReadyOnClient(true);
    }, []);

    const onSearch = () => {
      clearFilters();
      jobApplicationStore.clearAllSelected();
      return setQueryQueryParams.navigateToSearch(
        jobApplicationStore.searchText,
      );
    };

    const onChange = (text: string) => {
      jobApplicationStore.updateSearchText(text);
    };

    const filtersSearchBar = (
      <FilterSearchBar
        onChange={onChange}
        value={jobApplicationStore.searchText}
        lastResultSearchText={jobApplicationStore.lastResultSearchText}
        placeholder={t('Search names')}
        onSearch={onSearch}
      />
    );

    if (!isReadyOnClient) {
      return (
        <Layout
          filterSearchBar={filtersSearchBar}
          bucketSelect={null}
          filters={null}
        />
      );
    }

    return (
      <Layout
        filterSearchBar={filtersSearchBar}
        bucketSelect={<BucketSelect />}
        filters={
          <Filters
            isMustHaveToggleSelected={isMustHaveToggleSelected}
            questions={questions}
            onMustHaveFilterToggled={onMustHaveFilterToggled}
            onParentFilterSelected={onParentFilterSelected}
            onFilterSelected={onFilterSelected}
          />
        }
      />
    );
  },
);

const Layout: FC<{
  filterSearchBar: ReactNode;
  bucketSelect: ReactNode;
  filters: ReactNode;
}> = ({ filterSearchBar, bucketSelect, filters }) => (
  <Stack space="large">
    <Stack space="gutter">
      <>{filterSearchBar}</>
      <>{bucketSelect}</>
    </Stack>
    <>{filters}</>
  </Stack>
);
